import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, ShieldCheck, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useConfig, waLink } from "@/context/ConfigContext";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "Properties", to: "/properties" },
  { label: "About Us", to: "/#about" },
  { label: "Investment Opportunities", to: "/#investment" },
  { label: "Locations", to: "/#locations" },
  { label: "Contact", to: "/#contact" },
];

export default function Footer() {
  const config = useConfig();

  return (
    <footer data-testid="site-footer" className="bg-navy text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-gold text-navy">
                <ShieldCheck className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="leading-tight">
                <p className="font-display text-lg font-bold">Exserviceman</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light">
                  Property Services
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/60">
              Residential &bull; Commercial &bull; Land Investment
            </p>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Premium plot opportunities in strategically connected locations around Nagpur, Maharashtra.
            </p>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Quick Links</h3>
            <ul className="mt-5 space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/70 transition-colors hover:text-gold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Contact</h3>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
                <span data-testid="footer-phone">{config.phone}</span>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
                <a
                  href={waLink(config)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="transition-colors hover:text-gold"
                >
                  WhatsApp Us
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
                <span data-testid="footer-email">{config.email}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
                <span>Nagpur, Maharashtra, India</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Follow Us</h3>
            <div className="mt-5 flex gap-3">
              {[
                { icon: Instagram, label: "Instagram", id: "instagram" },
                { icon: Facebook, label: "Facebook", id: "facebook" },
                { icon: Youtube, label: "YouTube", id: "youtube" },
              ].map(({ icon: Icon, label, id }) => (
                <a
                  key={id}
                  href="#"
                  aria-label={label}
                  data-testid={`social-${id}`}
                  className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/15 text-white/70 transition-colors hover:border-gold hover:text-gold"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="text-xs leading-relaxed text-white/45" data-testid="footer-disclaimer">
            Property information, pricing, availability, approvals and other details are subject to verification
            and may change. Buyers are advised to independently verify title, ownership, approvals, documentation,
            zoning, access and other legal/property-related details before making any transaction.
          </p>
          <div className="mt-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <p className="text-xs text-white/50">
              &copy; 2026 Exserviceman Property Services. All Rights Reserved.
            </p>
            <div className="flex gap-6 text-xs text-white/50">
              <Link to="/privacy-policy" data-testid="footer-privacy-link" className="transition-colors hover:text-gold">
                Privacy Policy
              </Link>
              <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-gold">
                Terms &amp; Conditions
              </Link>
              <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="transition-colors hover:text-gold">
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
